'use strict';

const _ = require('lodash');
const moment = require('moment-timezone');

//身分證號等價表
const alphaVal = [
	'BNZ', 'AMW', 'KLY', 'JVX', 'HU', 'GT', 'FS', 'ER', 'DOQ', 'CIP'
].reduce((previousValue, currentValue, currentIndex) => {
	[...currentValue].forEach(c => previousValue[c] = currentIndex);
	return previousValue;
}, {});

const Formatter = {
	currency: (value, code) => {
		if (_.isNil(value)) return '';
		const CURRENCIES = {
			TWD: { prefix: 'NT$', decimals: 0 },
			USD: { prefix: '$', decimals: 2 },
			EUR: { prefix: '€', decimals: 2 },
			CNY: { prefix: '¥', decimals: 2 },
			HKD: { prefix: '$', decimals: 2 },
			JPY: { prefix: '¥', decimals: 0 },
			THB: { prefix: '฿', decimals: 0 },
			IDR: { prefix: '₨', decimals: 0 },
			VND: { prefix: '₫', decimals: 0 },
		};
		if (!_.includes(_.keys(CURRENCIES), code.toUpperCase())) code = 'TWD';
		return (value * 1).format(CURRENCIES[code]);
	},

	/**
	 * 把下列格式的字串轉成 moment 物件，或是 undefined
	 * <p>p.s. 如果只是操作完後馬上要再轉成字串，也可以直接用 moment(str, fmt) 就好</p>
	 * <ul>
	 *     <li>YYYY/MM/DD</li>
	 *     <li>YYYY/MM/DD HH:mm</li>
	 *     <li>YYYY/MM/DD HH:mm:ss</li>
	 * </ul>
	 * @param {string} str
	 * @param {string} [timezone]
	 * @returns {undefined|moment.Moment}
	 * @see http://momentjs.com/guides/#/warnings/js-date/
	 */
	strToMoment: (str, timezone) => {
		let m = moment.tz(str, ['YYYY/MM/DD', 'YYYY/MM/DD HH:mm', 'YYYY/MM/DD HH:mm:ss'], timezone);
		if (m.isValid()) return m;
		return undefined;
	},

	/**
	 * @param {*} src 任何 moment 能 parse 的東西
	 * @param {string} [delimiter] 年月日之間的分隔符號，預設是斜線 '/'
	 * @returns {string} YYYY/MM/DD 格式的字串
	 */
	date: (src, delimiter = '/') => {
		if (!src || src === '0000-00-00' || src === '9999-99-99' || src === '9999-12-31') return '';
		return moment(src).format(`YYYY${delimiter}MM${delimiter}DD`);
	},

	YYYYMM: src => {
		if (!src || src === '0000-00-00' || src === '9999-99-99' || src === '9999-12-31') return '';
		return moment(src).format('YYYY/MM');
	},

	dateRange: src => {
		if (!_.isArray(src)) return;
		const _d = Formatter.date, d1 = _d(src[0]), d2 = _d(src[1]);
		return d1 && d2 ? `${d1} ~ ${d2}` : d1 ? d1 : d2 ? d2 : '';
	},

	/**
	 * @param {*} src 任何 moment 能 parse 的東西
	 * @param {boolean} isSecond 回傳值是否包含 "秒"
	 * @returns {string} HH:mm:ss 或 HH:mm 格式的字串
	 */
	time: (src, isSecond = true) => {
		if (!src || src === '0000-00-00' || src === '9999-99-99' || src === '9999-12-31') return '';
		return moment(src).format(isSecond ? 'HH:mm:ss' : 'HH:mm');
	},

	/**
	 * 將 src 轉成 local time 後回傳字串。<br/>
	 * 若有提供 timezone，則將 src 轉成 local time 再轉成指定 timezone 後才回傳字串。
	 * @param {*} src 任何 moment 能 parse 的東西
	 * @param {boolean} isSecond 回傳值是否包含 "秒"
	 * @param {string} [timezone] 時區 (例如 'Asia/Taipei')
	 * @param {string} [delimiter] 年月日之間的分隔符號，預設是斜線 '/'
	 * @returns {string} YYYY/MM/DD HH:mm:ss 格式的字串
	 */
	dateTime: (src, isSecond = true, timezone, delimiter = '/') => {
		if (!src || src === '0000-00-00' || src === '9999-99-99' || src === '9999-12-31') return '';
		let localTime = moment(src);
		if (timezone) localTime.tz(timezone);
		return localTime.format(`YYYY${delimiter}MM${delimiter}DD HH:mm${isSecond ? ':ss' : ''}`);
	},

	/**
	 * @param {*} src 任何 moment 能 parse 的東西
	 * @param {object} [options]
	 * @param {string} [options.timezone] 時區 (例如 'Asia/Taipei')
	 * @param {string} [options.delimiter] 年月日之間的分隔符號，預設是斜線 '/'
	 * @return {string} YYYY/MM 格式的字串
	 */
	yearMonth: (src, options) => {
		const { timezone, delimiter = '/' } = options || {};
		if (!src || src === '0000-00-00' || src === '9999-99-99' || src === '9999-12-31') return '';
		let localTime = moment(src);
		if (timezone) localTime.tz(timezone);
		return localTime.format(`YYYY${delimiter}MM`);
	},

	/**
	 * 數字格式化：加上千分位、去小數尾 0、0 值轉空白
	 * @param {number} src 傳入欄位值
	 * @returns {string} 結果字串 #,###.0
	 */
	numeric: src => {
		const parts = (+src + '').split('.');
		if (parts[0] === 'NaN')
			return src;
		if (+parts[0] || parts[1]) {
			parts[0] = parts[0].replace(/(\d)(?=(\d{3})+$)/g, '$1,');
			return parts.join('.');
		}
		return '';
	},

	/**
	 * 字串左邊補 0
	 * @param {string | number} v 原始值
	 * @param {number} d 目標長度, 預設為 2
	 * @returns {string} 結果字串
	 */
	fDigit: (v, d = 2) => ('' + v).padStart(d, '0'),

	/**
	 * 挽救 J104Time 回傳值儲存的問題
	 * @param {number|number[]} list J104Time 回傳的陣列
	 * @returns {string[]} 可供後端儲存的陣列
	 */
	fixTimes: list => {
		if (!Array.isArray(list)) list = [list];
		return list.reduce((p, c) => {
			const dt = new Date(c);
			if (dt.isValid()) {
				p.push([
					Utils.Format.fDigit(dt.getHours()),
					Utils.Format.fDigit(dt.getMinutes()),
				].join(':'));
			}

			return p;
		}, []);
	},

	/**
	 * 修正 44.4+33.3+22.3<100
	 * @param {number} v 十進位計算浮點值
	 * @param {number} p 目標精度, 預設為 4
	 * @returns {number} 依精度修正結果, 任一參數 NaN 則結果為 NaN
	 */
	fixed: (v, p = 4) => Math.round(v * `1e${p}`) / `1e${p}`,

	/**
	 * 判斷身分證號
	 * @param {string} idc 身分證號
	 * @deprecated use @104ha-utils/commonUtils.checkIdcNo instead.
	 */
	checkIdcNo: idc => {
		if (!/^[A-Z][1289]\d{8}$/.test(idc)) return false;

		const result = [...idc.toUpperCase()].reduce((previousValue, currentValue, currentIndex) => {
			if (currentIndex) previousValue += Math.max(9 - currentIndex, 1) * currentValue;
			else previousValue += alphaVal[currentValue];

			return previousValue;
		}, 0);
		return result % 10 ? false : +idc.charAt(1);
	},

	/**
	 * 判斷外僑統一證號規則
	 * @param {string} idc 外僑統一證號
	 * @deprecated use @104ha-utils/commonUtils.checkAlienIdc instead.
	 */
	checkAlienIdc: idc => {
		return /^(\d{8}([A-Z]{2})?|[A-Z][89A-D]\d{8})$/.test(idc);

		const sexVal = { A: 0, B: 1, C: 2, D: 3 };
		const result = [...idc.toUpperCase()].reduce((previousValue, currentValue, currentIndex) => {
			if (currentIndex > 1) previousValue += Math.max(9 - currentIndex, 1) * currentValue;
			else if (currentIndex) previousValue += sexVal[currentValue] * 8;
			else previousValue += alphaVal[currentValue];

			return previousValue;
		}, 0);
		return result % 10 ? false : sexVal[idc.charAt(1)] % 2 + 1;
	},

	// 傳入日期 YYYY-MM-DD、時間 HH:mm:ss、時區 'Asia/Taipei'
	toDateTimeStrWithTimeZone: (date, time, timeZone) => {
		const offset = moment.tz(new Date(), timeZone).utcOffset();
		const mins = Math.abs(offset % 60);
		const hours = Math.floor(Math.abs(offset / 60));
		const timeZoneOffset = `${offset < 0 ? '-' : '+'}${hours < 10 ? `0${hours}` : hours}:${mins < 10 ? `0${mins}` : mins}`;

		return `${date.replace(/\//g, '-')}T${time}${timeZoneOffset}`;
	}
};

module.exports = Formatter;
